import { getPlayerFullName, playerMatchesSearchQuery } from './playerUtils';

/**
 * Whether a nested `players` row should appear in club search/leaderboard lists.
 * Excludes inactive, soft-deleted, and merged/archived guests even if the
 * `players_club` membership row is still marked active.
 */
export function isSelectableClubRosterPlayer(player) {
  if (!player?.id) return false;
  if (player.is_active === false) return false;
  if (player.deletion_requested_at != null) return false;
  if (
    player.is_guest === true &&
    (player.merged_at != null || player.merged_into_user_id != null)
  ) {
    return false;
  }
  return true;
}

/**
 * Whether a player should appear in matchup record search (head-to-head picker).
 * Hides archived guests (inactive player row), soft-deleted guests, merged guests,
 * and guests with no active membership in the clubs being searched.
 * Members stay searchable even when their club membership is inactive.
 *
 * @param {object|null|undefined} player
 * @param {{ hasActiveClubMembership?: boolean }} [options]
 */
export function isSelectableMatchupSearchPlayer(player, { hasActiveClubMembership = true } = {}) {
  if (!player?.id) return false;
  if (player.is_guest !== true) return true;
  if (player.is_active === false) return false;
  if (player.deletion_requested_at != null) return false;
  if (player.merged_at != null || player.merged_into_user_id != null) return false;
  if (!hasActiveClubMembership) return false;
  return true;
}

/**
 * Map a players_club row (with nested `players`) to PlayerSearchInput shape.
 * Returns null when the row has no usable display name.
 */
export function normalizeClubPlayerForSearch(row) {
  const player = row?.players;
  const id = row?.player_id || player?.id;
  if (!id || !player) return null;

  const displayName = getPlayerFullName(player);
  if (!displayName || displayName === 'Unknown') return null;

  return {
    id,
    first_name: (player.first_name || '').trim(),
    last_name: (player.last_name || '').trim(),
    gender: player.gender,
    email: player.email || '',
    name: displayName,
  };
}

export function sortClubPlayersByName(players) {
  return [...players].sort((a, b) =>
    getPlayerFullName(a).localeCompare(getPlayerFullName(b), undefined, { sensitivity: 'base' })
  );
}

export { getPlayerFullName, playerMatchesSearchQuery };
